import React from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";

const TABS = [
    { id: "home", label: "Home" },
    { id: "library", label: "Puzzles" },
    { id: "settings", label: "Settings" },
];

const HERO_BOXES = [
    { top: "4%", left: "6%", size: 54, rotate: -16, opacity: 0.55, outline: false },
    { top: "18%", right: "24%", size: 38, rotate: 22, opacity: 0.5, outline: true },
    { top: "8%", right: -22, size: 92, rotate: 14, opacity: 0.38, outline: false },
    { top: "28%", left: -20, size: 84, rotate: -18, opacity: 0.34, outline: true },
    { top: "6%", left: "46%", size: 34, rotate: 10, opacity: 0.48, outline: false },
    { top: "36%", left: "18%", size: 72, rotate: -10, opacity: 0.22, outline: false },
    { top: "42%", right: "8%", size: 64, rotate: 16, opacity: 0.28, outline: true },
    { top: "52%", left: "58%", size: 48, rotate: -8, opacity: 0.32, outline: false },
];

/**
 * Light shell from the Android renewal: sage hero and Home / Puzzles / Settings.
 */
export default function ScreenshotAppChrome({ active, title, children }) {
    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                bgcolor: "#f2f2f7",
                color: "#0f1419",
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "50%",
                    overflow: "hidden",
                    background: "linear-gradient(180deg, #b7d4c8 0%, #d4e6dc 58%, #f2f2f7 100%)",
                    pointerEvents: "none",
                }}
            >
                {HERO_BOXES.map((box, index) => (
                    <Box
                        key={`hero-box-${index}`}
                        sx={{
                            position: "absolute",
                            top: box.top,
                            left: box.left,
                            right: box.right,
                            width: box.size,
                            height: box.size,
                            opacity: box.opacity,
                            borderRadius: "10px",
                            transform: `rotate(${box.rotate}deg)`,
                            bgcolor: box.outline ? "transparent" : "rgba(255,255,255,0.55)",
                            border: box.outline ? "1.5px solid rgba(255,255,255,0.55)" : "none",
                        }}
                    />
                ))}
            </Box>
            {title ? (
                <Typography
                    component="h1"
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        m: 0,
                        px: 2,
                        pt: "28px",
                        minHeight: 48,
                        display: "flex",
                        alignItems: "center",
                        fontSize: 17,
                        fontWeight: 800,
                        letterSpacing: 0,
                        lineHeight: 1.2,
                        color: "#0f1419",
                    }}
                >
                    {title}
                </Typography>
            ) : null}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: "1 1 auto",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {children}
            </Box>
            <Box
                component="nav"
                aria-label="App menu"
                sx={{
                    position: "relative",
                    zIndex: 2,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "stretch",
                    minHeight: 56,
                    pb: "10px",
                    bgcolor: "#ffffff",
                    borderTop: "1px solid rgba(15, 23, 42, 0.1)",
                }}
            >
                {TABS.map((tab) => {
                    const selected = tab.id === active;
                    return (
                        <Box
                            key={tab.id}
                            sx={{
                                flex: 1,
                                minHeight: 56,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 0.25,
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: 14,
                                    fontWeight: selected ? 800 : 700,
                                    lineHeight: "18px",
                                    textAlign: "center",
                                    color: selected ? "#3BA666" : "rgba(15, 23, 42, 0.45)",
                                }}
                            >
                                {tab.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

ScreenshotAppChrome.propTypes = {
    active: PropTypes.oneOf(["home", "library", "settings"]).isRequired,
    title: PropTypes.string,
    children: PropTypes.node,
};
